/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreateImageEmbeddingRequest } from '../models/CreateImageEmbeddingRequest';
import type { CreateVideoEmbeddingRequest } from '../models/CreateVideoEmbeddingRequest';
import type { EmbeddingGenerationStatus } from '../models/EmbeddingGenerationStatus';
import type { ImageEmbeddingNotAvailableResponse } from '../models/ImageEmbeddingNotAvailableResponse';
import type { ImageMetadataRequest } from '../models/ImageMetadataRequest';
import type { ImageResponse } from '../models/ImageResponse';
import type { Page_ImageResponse_ } from '../models/Page_ImageResponse_';
import type { Page_ImageSearchResponse_ } from '../models/Page_ImageSearchResponse_';
import type { Page_ImageWithEmbeddingResponse_ } from '../models/Page_ImageWithEmbeddingResponse_';
import type { Page_str_ } from '../models/Page_str_';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ImageEmbeddingService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get Embeddings
     * @returns Page_ImageWithEmbeddingResponse_ Successful Response
     * @throws ApiError
     */
    public getEmbeddings({
        url,
        embeddingGenerationStatus,
        imageId,
        sources,
        extraJsonpathExclude,
        extraJsonpathInclude,
        page = 1,
        size = 50,
    }: {
        url?: string,
        embeddingGenerationStatus?: EmbeddingGenerationStatus,
        imageId?: string,
        sources?: Array<string>,
        extraJsonpathExclude?: string,
        extraJsonpathInclude?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<Page_ImageWithEmbeddingResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/image_embedding/',
            query: {
                'url': url,
                'embedding_generation_status': embeddingGenerationStatus,
                'image_id': imageId,
                'sources': sources,
                'extra_jsonpath_exclude': extraJsonpathExclude,
                'extra_jsonpath_include': extraJsonpathInclude,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Embedding
     * @returns ImageResponse Successful Response
     * @throws ApiError
     */
    public generateEmbedding({
        requestBody,
    }: {
        requestBody: CreateImageEmbeddingRequest,
    }): CancelablePromise<Array<ImageResponse>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/image_embedding/',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Image Metadata
     * @returns ImageResponse Successful Response
     * @throws ApiError
     */
    public updateImageMetadata({
        requestBody,
    }: {
        requestBody: Array<ImageMetadataRequest>,
    }): CancelablePromise<Array<ImageResponse>> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/image_embedding/',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Video Embedding
     * @returns ImageResponse Successful Response
     * @throws ApiError
     */
    public generateVideoEmbedding({
        requestBody,
    }: {
        requestBody: CreateVideoEmbeddingRequest,
    }): CancelablePromise<Array<ImageResponse>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/image_embedding/video/',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Image Metadata
     * @returns Page_ImageResponse_ Successful Response
     * @throws ApiError
     */
    public getImageMetadata({
        url,
        embeddingGenerationStatus,
        imageId,
        sources,
        extraJsonpathExclude,
        extraJsonpathInclude,
        page = 1,
        size = 50,
    }: {
        url?: string,
        embeddingGenerationStatus?: EmbeddingGenerationStatus,
        imageId?: string,
        sources?: Array<string>,
        extraJsonpathExclude?: string,
        extraJsonpathInclude?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<Page_ImageResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/image_embedding/metadata',
            query: {
                'url': url,
                'embedding_generation_status': embeddingGenerationStatus,
                'image_id': imageId,
                'sources': sources,
                'extra_jsonpath_exclude': extraJsonpathExclude,
                'extra_jsonpath_include': extraJsonpathInclude,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get List Of Sources
     * @returns Page_str_ Successful Response
     * @throws ApiError
     */
    public getListOfSources({
        page = 1,
        size = 50,
    }: {
        page?: number,
        size?: number,
    }): CancelablePromise<Page_str_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/image_embedding/sources',
            query: {
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Similar Images
     * @returns Page_ImageSearchResponse_ Successful Response
     * @returns ImageEmbeddingNotAvailableResponse Accepted
     * @throws ApiError
     */
    public getSimilarImages({
        imageId,
        url,
        limitResultsToSources,
        extraJsonpathInclude,
        extraJsonpathExclude,
        page = 1,
        size = 50,
    }: {
        imageId?: string,
        url?: string,
        limitResultsToSources?: Array<string>,
        extraJsonpathInclude?: string,
        extraJsonpathExclude?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<Page_ImageSearchResponse_ | ImageEmbeddingNotAvailableResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/image_embedding/similar',
            query: {
                'image_id': imageId,
                'url': url,
                'limit_results_to_sources': limitResultsToSources,
                'extra_jsonpath_include': extraJsonpathInclude,
                'extra_jsonpath_exclude': extraJsonpathExclude,
                'page': page,
                'size': size,
            },
            errors: {
                400: `Bad Request`,
                404: `Not Found`,
                422: `Validation Error`,
            },
        });
    }

}
